<script setup>
import CloseButton from "@/components/modal/common/closeButton.vue";
import axios from "@/axios";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import MeetingSelectBox from "@/components/common/input/meetingSelectBox.vue";
import {useLanguageStore, useLoadingStore} from "@/pinia";
import {useToast} from "@/composables/useToast";
import {onMounted, ref, toRef} from "vue";
const loadingStore = useLoadingStore();
const languageStore = useLanguageStore();
const toast = useToast();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField')
};
const props=  defineProps({
  dataType : String,
  liveData : Object
})

const today = new Date();
const selectedDate = ref(null)
const selectedOption = ref(null)
const description = ref(null)
const liveIdIs = ref(null)
const meeting = ref(undefined)
const title = ref(undefined)
const meeting2 = ref( undefined)
const title2 = ref(undefined)
const description2 = ref(undefined)
const liveTitle = ref(null)
const privateStatus2 = ref(null)
const emits = defineEmits(['close', 'save', 'update'])

onMounted(()=>{
  if(props.liveData == null) return
  if(props.dataType === 'modify') {
    meeting2.value = (props.liveData.meeting != null ? props.liveData.meeting.meetingId : "")
    title2.value = props.liveData.liveTitle
    description2.value = props.liveData.liveDescription
    selectedOption.value = props.liveData.privateStatus
    selectedDate.value = props.liveData.liveStartDt
    liveIdIs.value = props.liveData.liveId
  }
})

const closeBtn= ()=> {
  emits('close');
}
const saveModal = async ()=>{
    // v-form validation
    const {valid} = await formRef.value.validate();

    // Custom validation for non-Vuetify components
    if (!meeting.value) {
      toast.warning(languageStore.get('pleaseSelectMeeting'));
      return;
    }
    if (!selectedOption.value) {
      toast.warning(languageStore.get('pleaseSelectPublicStatus'));
      return;
    }
    if (!selectedDate.value) {
      toast.warning(languageStore.get('pleaseSelectDate'));
      return;
    }

    if (!valid) {
      toast.warning(languageStore.get('pleaseCheckInputFields'));
      return;
    }

    const dataList = {
        meetingId : meeting.value,
        privateStatus : selectedOption.value,
        liveStartDt : selectedDate.value,
        liveTitle : title.value,
        liveDescription : description.value
    }
    emits('save',dataList);
}

const updateModal= async ()=> {
    // v-form validation
    const {valid} = await formRef.value.validate();

    if (!selectedOption.value) {
      toast.warning(languageStore.get('pleaseSelectPublicStatus'));
      return;
    }
    if (!selectedDate.value) {
      toast.warning(languageStore.get('pleaseSelectDate'));
      return;
    }

    if (!valid) {
      toast.warning(languageStore.get('pleaseCheckInputFields'));
      return;
    }

    const liveUpdate = {
        liveTitle : title2.value,
        liveDescription : description2.value,
        privateStatus : selectedOption.value,
        liveStartDt : selectedDate.value,
        meetingId : meeting.value || meeting2.value,
    }
    emits("update", liveUpdate, liveIdIs.value);
}
const set_meeting = (newMeeting) =>{
    meeting.value = newMeeting;
}


</script>

<template>
<div class="modal">
    <div class="modal-content">
        <div v-if="dataType === 'modify'">
          <div class="videoTitle">{{languageStore.get('scheduledLiveMeeting')}} {{languageStore.get('edit')}}</div>
        </div>
        <div v-else-if="dataType === 'regist'">
            <div class="videoTitle">{{languageStore.get('scheduledLiveMeeting')}} {{languageStore.get('register')}}</div>
        </div>
        <!-- 공통 -->
        <div class="modal-body">
          <v-form ref="formRef" v-model="isFormValid">
            <template v-if="dataType=== 'regist'">
              <MeetingSelectBox
                  v-model="meeting"
                  @set_meetingId="set_meeting"
                  class="mb-4"
              ></MeetingSelectBox>
            </template>
            <template v-else-if="dataType === 'modify'">
              <MeetingSelectBox
                  v-model="meeting2"
                  @set_meetingId="set_meeting"
                  class="mb-4"
              ></MeetingSelectBox>
            </template>

            <template v-if="dataType=== 'regist'">
              <v-textarea
                  v-model="title"
                  :label="languageStore.get('title') + ' *'"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  hide-details="auto"
                  class="mb-4"
                  :rules="[rules.required]"
              ></v-textarea>
            </template>
            <template v-else-if="dataType === 'modify'">
              <v-textarea
                  v-model="title2"
                  :label="languageStore.get('title') + ' *'"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  hide-details="auto"
                  class="mb-4"
                  :rules="[rules.required]"
              ></v-textarea>
            </template>

            <template v-if="dataType=== 'regist'">
              <v-textarea
                  v-model="description"
                  :label="languageStore.get('note')"
                  variant="outlined"
                  density="compact"
                  rows="3"
                  hide-details="auto"
                  class="mb-4"
              ></v-textarea>
            </template>
            <template v-else-if="dataType === 'modify'">
              <v-textarea
                  v-model="description2"
                  :label="languageStore.get('note')"
                  variant="outlined"
                  density="compact"
                  rows="3"
                  hide-details="auto"
                  class="mb-4"
              ></v-textarea>
            </template>

            <v-radio-group
                v-model="selectedOption"
                :label="languageStore.get('publicStatus') + ' *'"
                inline
                hide-details
                class="mb-4"
            >
              <v-radio :label="languageStore.get('public')" value="public"></v-radio>
              <v-radio :label="languageStore.get('partiallyPublic')" value="unlisted"></v-radio>
              <v-radio :label="languageStore.get('private')" value="private"></v-radio>
            </v-radio-group>

            <VueDatePicker
                class="dp-full-width"
                locale="ko"
                v-model="selectedDate"
                :min-date="today"
                :is-24="false"
                :placeholder="languageStore.get('dateTime')"
                :teleport="true"
            />

          </v-form>

            <div class="modal-actions">
              <v-progress-circular
                  v-show="loadingStore.isLoading"
                  color="#052C65"
                  indeterminate
              ></v-progress-circular>
              <v-btn
                  v-if="dataType === 'regist'"
                  v-show="!loadingStore.isLoading"
                  color="primary"
                  variant="flat"
                  @click="saveModal"
              >{{ languageStore.get('register') }}</v-btn>
              <v-btn
                  v-else-if="dataType === 'modify'"
                  v-show="!loadingStore.isLoading"
                  color="primary"
                  variant="flat"
                  @click="updateModal"
              >{{ languageStore.get('edit') }}</v-btn>
              <v-btn variant="outlined" @click="closeBtn">{{ languageStore.get('close') }}</v-btn>
            </div>
        </div>

        <!-- Toast -->
        <v-snackbar v-model="toast.show.value" :color="toast.color.value" :timeout="toast.timeout.value" location="top">
          {{ toast.message.value }}
          <template v-slot:actions>
            <v-btn variant="text" @click="toast.close()">{{ languageStore.get('close') }}</v-btn>
          </template>
        </v-snackbar>
    </div>
</div>
</template>

<style scoped>



.btnCal{
 min-width: 18vw;
 position: relative;
 right: 2%;
}
.btnCal button{
 white-space: nowrap;
 padding: 0 12px;
}

.bottomBtns{
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 25px;
}
.radioBtns {
    padding: 5px;
}

.textArea1, .textArea2{
    min-width: 18vw;
    max-height: 1.5vw;
    margin: 10px 10px 10px 20px;
    padding: 10px;
}

.textArea2M {
    min-width: 18vw;
    max-height: 2vw;
    margin: 10px 10px 10px 20px;
    padding: 10px;
}

.meetingBox{
    padding: 5px;
    min-width: 19.3vw;
    position: relative;
    right: 2%;
}

textarea {
    resize : none;
}

.radioBtn {
    display: flex;
}

.content {
    display: flex;
    margin: 10px;
    align-items: center;
}

.nameText {
    padding: 10px;
    font-weight: 300;
    width: 110px;
}

.videoTitle {
    padding: 16px 24px;
    background-color: #1A3A5C;
    color: #FFFFFF;
    text-align: center;
    font-weight: 600;
    font-size: 18px;
}

/* 모달 본문 */
.modal-body {
  padding: 24px;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input),
.modal-body :deep(.dp-full-width) {
  margin-bottom: 16px;
}

/* DatePicker 스타일 */
.dp-full-width {
  width: 100%;
}

.dp-full-width :deep(.dp__input) {
  height: 40px;
  border-radius: 4px;
  border-color: #C7C7C7;
}

/* 모달 액션 버튼 */
.modal-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #DEE2E6;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  width: 600px;
  overflow: hidden;
}





</style>